import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';

export const AI_FUNDING_MODES = Object.freeze({
  PLATFORM_WALLET: 'platform_wallet',
  TENANT_BYOK: 'tenant_byok',
});

export const normalizeUniversityAiFundingMode = (value) => (
  value === AI_FUNDING_MODES.TENANT_BYOK
    ? AI_FUNDING_MODES.TENANT_BYOK
    : AI_FUNDING_MODES.PLATFORM_WALLET
);

const FundingOption = ({ value, selected, title, description, badge }) => (
  <Paper
    variant="outlined"
    sx={{
      flex: 1,
      minWidth: 0,
      borderColor: selected ? 'primary.main' : 'divider',
      bgcolor: selected ? 'action.selected' : 'background.paper',
    }}
  >
    <FormControlLabel
      value={value}
      control={<Radio />}
      sx={{
        alignItems: 'flex-start',
        m: 0,
        p: 2,
        width: '100%',
        '& .MuiFormControlLabel-label': { minWidth: 0 },
      }}
      label={(
        <Stack spacing={0.5} sx={{ pt: 0.25 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2">{title}</Typography>
            <Chip size="small" label={badge} color={selected ? 'primary' : 'default'} variant={selected ? 'filled' : 'outlined'} />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Stack>
      )}
    />
  </Paper>
);

const PlatformConfigurationSummary = ({ config }) => {
  const realtime = config?.realtime || {};
  const marking = config?.marking || {};
  const transcription = config?.transcription || {};
  const summary = [
    ['Realtime', realtime.provider, realtime.realtimeModel],
    ['Marking', marking.provider, marking.markingModel],
    ['Transcription', transcription.enabled === false ? 'Disabled' : transcription.provider, transcription.model],
  ];

  return (
    <Stack spacing={1.5}>
      <Alert severity="info">
        This university uses platform-managed AI. Provider credentials and workload defaults follow the global configuration automatically, and usage is charged to its platform credit wallet.
      </Alert>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        {summary.map(([label, provider, model]) => (
          <Paper key={label} variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
              {provider || 'Global default'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {model || 'Global model'}
            </Typography>
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
};

const UniversityAiConfigurationPanel = ({
  alert,
  children,
  config,
  fundingMode,
  loading = false,
  saving = false,
  onAlertClose,
  onFundingModeChange,
  onReset,
  onSave,
}) => {
  const normalizedMode = normalizeUniversityAiFundingMode(fundingMode);
  const isTenantByok = normalizedMode === AI_FUNDING_MODES.TENANT_BYOK;

  return (
    <Paper elevation={0} sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle1">AI funding and configuration</Typography>
          <Typography variant="body2" color="text.secondary">
            Choose who supplies AI credentials and pays for this university's usage.
          </Typography>
        </Box>

        {alert && (
          <Alert severity={alert.severity} onClose={onAlertClose}>
            {alert.message}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Stack spacing={3}>
            <FormControl component="fieldset">
              <RadioGroup
                aria-label="University AI funding mode"
                name="university-ai-funding-mode"
                value={normalizedMode}
                onChange={(event) => onFundingModeChange(event.target.value)}
              >
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                  <FundingOption
                    value={AI_FUNDING_MODES.PLATFORM_WALLET}
                    selected={!isTenantByok}
                    title="Platform funded"
                    badge="Uses credits"
                    description="Use platform-managed providers and keys. The university buys or receives platform credits."
                  />
                  <FundingOption
                    value={AI_FUNDING_MODES.TENANT_BYOK}
                    selected={isTenantByok}
                    title="University API keys"
                    badge="No credit charge"
                    description="The university supplies isolated provider keys and manages its models. Platform credits are not consumed."
                  />
                </Stack>
              </RadioGroup>
            </FormControl>

            {isTenantByok ? (
              <Stack spacing={3}>
                <Alert severity="warning">
                  Tenant keys are used only for this university. Add credentials for every enabled workload; platform credentials are never used as a fallback in this mode.
                </Alert>
                {children}
              </Stack>
            ) : (
              <PlatformConfigurationSummary config={config} />
            )}

            <Stack direction="row" spacing={2}>
              <Button variant="contained" onClick={onSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save AI settings'}
              </Button>
              <Button variant="outlined" onClick={onReset} disabled={saving}>
                Reset
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default UniversityAiConfigurationPanel;
